import { money, pct } from './format.js';

// The nine box grid, kept from the Morningstar snapshot at advisors' request.
//
// It earns its place on one sentence: you are retiring in three years and you
// are invested like someone with thirty years left. Neither the allocation
// donut nor the look-through can make that point, because both answer "what do
// you own" rather than "what kind of risk is inside it".
//
// Reads as a heat grid rather than a table of numbers, because the shape is the
// finding. A column of colour down Growth, or an empty middle row, is legible
// in a second on a screen share.

const SIZES = ['Large', 'Mid', 'Small'];
const KINDS = ['Value', 'Blend', 'Growth'];

// Tint strength by share of the placed equity, capped so one dominant cell does
// not wash the rest out.
function shade(p) {
  if (p <= 0) return 'transparent';
  const a = Math.min(0.85, 0.12 + (p / 45) * 0.73);
  return `rgba(1, 86, 151, ${a.toFixed(3)})`;
}

export default function StyleBox({ box }) {
  if (!box) return null;
  const { grid, rowPct, colPct, heaviest, coveragePct, unplaced, unplacedValue } = box;

  const emptyRow = SIZES.find(s => rowPct[s] === 0);
  const topStyle = KINDS.reduce((a, b) => (colPct[b] > colPct[a] ? b : a), KINDS[0]);
  const topSize = SIZES.reduce((a, b) => (rowPct[b] > rowPct[a] ? b : a), SIZES[0]);

  return (
    <section className="asmt-card" id="style-box">
      <div className="asmt-card-head">
        <h2>What kind of companies you own</h2>
        <span className="asmt-card-note">{pct(coveragePct, 0)} of the portfolio placed</span>
      </div>
      <p className="asmt-lede">
        Two questions decide how a stock portfolio behaves: how big the companies are,
        and whether they are priced as bargains or as growth stories. Every fund you
        hold lands somewhere on this grid.
      </p>

      <div className="asmt-sb-wrap">
        <table className="asmt-sb">
          <thead>
            <tr>
              <th />
              {KINDS.map(k => <th key={k} scope="col">{k}</th>)}
              <th className="asmt-sb-edge" scope="col">All</th>
            </tr>
          </thead>
          <tbody>
            {grid.map((row, i) => (
              <tr key={SIZES[i]}>
                <th scope="row">{SIZES[i]}</th>
                {row.map(cell => (
                  <td
                    key={cell.style}
                    className={`asmt-sb-cell${cell === heaviest ? ' is-heaviest' : ''}`}
                    style={{ background: shade(cell.pct) }}
                  >
                    {cell.pct >= 0.05 ? pct(cell.pct, 1) : '·'}
                  </td>
                ))}
                <td className="asmt-sb-edge">{pct(rowPct[SIZES[i]], 0)}</td>
              </tr>
            ))}
            <tr>
              <th scope="row" className="asmt-sb-edge">All</th>
              {KINDS.map(k => <td key={k} className="asmt-sb-edge">{pct(colPct[k], 0)}</td>)}
              <td />
            </tr>
          </tbody>
        </table>
      </div>

      <div className="asmt-callout">
        <strong>
          {pct(rowPct[topSize], 0)} of your stock money is in {topSize.toLowerCase()} companies
          {colPct[topStyle] >= 50 ? `, and ${pct(colPct[topStyle], 0)} of it is ${topStyle.toLowerCase()}` : ''}.
        </strong>{' '}
        {emptyRow
          ? `There is nothing at all in ${emptyRow.toLowerCase()} companies. `
          : ''}
        Whether that is right depends on how long this money has to last, which is the
        conversation rather than the chart.
      </div>

      {/* Stated plainly, because a grid that quietly covered two thirds of the
          portfolio while looking like all of it would be the wrong impression. */}
      {unplaced.length > 0 && (
        <p className="asmt-note" style={{ marginTop: 14 }}>
          {money(unplacedValue)} is not on this grid: {unplaced.map(u => u.assetClass).join(', ')}.
          Bonds, cash and target date funds have no size or style of their own, so placing
          them would be inventing a position rather than reporting one.
        </p>
      )}
    </section>
  );
}
